(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const staggerBlocks = new Set();
  const avatarGroups = new Set();
  const customStorySelector = '.neon-storefront, .automnia-routing, .automnia-infinite, .automnia-pricing';

  document.documentElement.classList.add('automnia-motion-ready');

  const getMotionValue = (name, fallback) => {
    const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  };

  const getMotionEase = (name, fallback) => {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
  };

  const show = (element) => {
    if (!(element instanceof HTMLElement)) return;
    element.classList.remove('is-hiding', 'is-shown');
    void element.offsetHeight;
    element.classList.add('is-shown');
  };

  const prepareStagger = (block) => {
    if (!(block instanceof HTMLElement) || block.dataset.motionPrepared === 'true') return;
    block.dataset.motionPrepared = 'true';
    block.classList.add('t-stagger');

    const lines = Array.from(block.children).filter((child) => {
      return child instanceof HTMLElement && child.getAttribute('aria-hidden') !== 'true' && !child.hasAttribute('data-motion-ignore');
    });

    if (lines.length) {
      lines.forEach((line, index) => {
        line.classList.add('t-stagger-line', `t-stagger-line--${index + 1}`);
        line.style.setProperty('--stagger-index', String(index + 1));
      });
    } else {
      block.classList.add('t-stagger-line', 't-stagger-line--1');
      block.style.setProperty('--stagger-index', '1');
    }

    staggerBlocks.add(block);
  };

  const prepareNativeSection = (section) => {
    if (!(section instanceof HTMLElement) || section.dataset.motionSectionPrepared === 'true') return;
    if (section.querySelector(customStorySelector)) return;

    section.dataset.motionSectionPrepared = 'true';
    section.classList.add('automnia-motion-section');
  };

  const resetAvatarGroup = (root) => {
    root.querySelectorAll('.t-avatar').forEach((avatar) => {
      avatar.style.transitionTimingFunction = getMotionEase('--avatar-ease-out', 'cubic-bezier(0.34, 3.85, 0.64, 1)');
      avatar.style.setProperty('--shift', '0px');
      avatar.style.setProperty('--scale-active', '1');
    });
  };

  const bindAvatarGroup = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.motionAvatarReady === 'true') return;
    root.dataset.motionAvatarReady = 'true';

    const avatars = Array.from(root.children).filter((child) => child instanceof HTMLElement);
    if (avatars.length < 2) return;

    avatars.forEach((avatar) => avatar.classList.add('t-avatar'));
    avatarGroups.add(root);

    if (!finePointer.matches || reduceMotion.matches) return;

    const setShifts = (activeIndex, phase) => {
      const lift = getMotionValue('--avatar-lift', -4);
      const falloff = getMotionValue('--avatar-falloff', 0.45);
      const scale = getMotionValue('--avatar-scale', 1.05);
      const easing = phase === 'out'
        ? getMotionEase('--avatar-ease-out', 'cubic-bezier(0.34, 3.85, 0.64, 1)')
        : getMotionEase('--avatar-ease-in', 'cubic-bezier(0.22, 1, 0.36, 1)');

      avatars.forEach((avatar, index) => {
        avatar.style.transitionTimingFunction = easing;
        const distance = activeIndex == null ? 0 : Math.abs(index - activeIndex);
        avatar.style.setProperty('--shift', `${(activeIndex == null ? 0 : lift * Math.pow(falloff, distance)).toFixed(3)}px`);
        avatar.style.setProperty('--scale-active', activeIndex === index ? String(scale) : '1');
      });
    };

    avatars.forEach((avatar, index) => {
      avatar.addEventListener('mouseenter', () => setShifts(index, 'in'));
    });
    root.addEventListener('mouseleave', () => setShifts(null, 'out'));
  };

  const motionObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        show(entry.target);
        motionObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })
    : null;

  const observe = (element) => {
    if (reduceMotion.matches || !motionObserver) {
      show(element);
    } else {
      motionObserver.observe(element);
    }
  };

  const initialize = (root = document) => {
    if (!(root instanceof Document || root instanceof HTMLElement)) return;

    root.querySelectorAll('[data-motion-reveal], [data-motion-stagger-group]').forEach(prepareStagger);

    root.querySelectorAll('#MainContent > .shopify-section').forEach((section) => {
      prepareNativeSection(section);
    });

    root.querySelectorAll('[data-motion-avatar-group]').forEach(bindAvatarGroup);

    staggerBlocks.forEach((block) => {
      if (!block.dataset.motionObserved) {
        block.dataset.motionObserved = 'true';
        observe(block);
      }
    });

    root.querySelectorAll('.automnia-motion-section').forEach((section) => {
      if (!section.dataset.motionObserved) {
        section.dataset.motionObserved = 'true';
        observe(section);
      }
    });
  };

  initialize();

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches('[data-motion-reveal], [data-motion-stagger-group]')) prepareStagger(node);
        if (node.matches('#MainContent > .shopify-section')) prepareNativeSection(node);
        if (node.matches('[data-motion-avatar-group]')) bindAvatarGroup(node);
        initialize(node);
      });
    });
  });

  if (document.body) mutationObserver.observe(document.body, { childList: true, subtree: true });

  const handleMotionPreferenceChange = () => {
    document.documentElement.classList.toggle('automnia-reduced-motion', reduceMotion.matches);
    if (reduceMotion.matches) {
      staggerBlocks.forEach(show);
      document.querySelectorAll('.automnia-motion-section').forEach(show);
      avatarGroups.forEach(resetAvatarGroup);
    }
  };

  handleMotionPreferenceChange();
  reduceMotion.addEventListener?.('change', handleMotionPreferenceChange);
  finePointer.addEventListener?.('change', () => {
    if (!finePointer.matches || reduceMotion.matches) avatarGroups.forEach(resetAvatarGroup);
  });
})();
